import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PhotoLibrary as PhotoLibraryIcon,
  CalendarToday as CalendarIcon,
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  HourglassEmpty as InProgressIcon,
  SkipNext as SkipIcon,
  PhotoAlbum as PhotoAlbumIcon
} from '@mui/icons-material';
import sessionService from '../api/sessionService';
import { useFavicon } from '../useFavicon';

const SESSION_FAVICON_URL = process.env.PUBLIC_URL + '/album-icon.png';

const SessionManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const albumRoute = (sessionId) =>
    user?.userType === 'album' ? `/album-wizard/${sessionId}` : `/image-upload/${sessionId}`;
  useFavicon(SESSION_FAVICON_URL);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialog, setCreateDialog] = useState({ open: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, session: null });
  const [deleteProgress, setDeleteProgress] = useState({ steps: [], isDeleting: false });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  // New session form data
  const [newSession, setNewSession] = useState({
    name: '',
    description: '',
    eventType: ''
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sessionService.getSessions({ limit: 100 });
      setSessions(response.sessions || []);
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async () => {
    if (!newSession.name.trim()) {
      alert('Session name is required');
      return;
    }

    try {
      const createdSession = await sessionService.createSession({
        name: newSession.name.trim(),
        description: newSession.description.trim(),
        settings: {
          eventType: newSession.eventType.trim()
        }
      });
      
      setCreateDialog({ open: false });
      setNewSession({ name: '', description: '', eventType: '' });
      
      // Navigate to the appropriate page for the new session
      navigate(albumRoute(createdSession.uniqueId));
    } catch (err) {
      alert('Failed to create session: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteDialog.session) return;

    setDeleteProgress({ steps: [], isDeleting: true });

    try {
      const response = await sessionService.deleteSession(deleteDialog.session.uniqueId);
      
      // Display the progress steps from the server
      if (response.progress && response.progress.steps) {
        setDeleteProgress({
          steps: response.progress.steps,
          isDeleting: false
        });
      }

      // Reload sessions in background
      loadSessions();

    } catch (err) {
      setDeleteProgress({ 
        steps: [{ 
          step: 'Error', 
          status: 'failed', 
          details: { error: err.message || 'Unknown error' } 
        }],
        isDeleting: false 
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'skipped':
        return <SkipIcon color="disabled" />;
      case 'in-progress':
        return <InProgressIcon color="primary" />;
      default:
        return <InProgressIcon />;
    }
  };

  const handleSelectSession = (session) => {
    navigate(albumRoute(session.uniqueId));
  };

  const handleMenuOpen = (event, session) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedSession(null);
  };

  const handleOpenFaceRecognition = () => {
    if (selectedSession) {
      navigate(`/face-recognition/${selectedSession.uniqueId}`);
    }
    handleMenuClose();
  };

  const handleCreateAlbum = () => {
    if (selectedSession) {
      navigate(`/album/${selectedSession.uniqueId}`);
    }
    handleMenuClose();
  };

  const handleStartAlbumWizard = (session) => {
    const target = session || selectedSession;
    if (target) {
      navigate(`/album-wizard/${target.uniqueId}`);
    }
    handleMenuClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Photo Sessions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog({ open: true })}
        >
          Create New Session
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Sessions Grid */}
      {sessions.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <FolderIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Sessions Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first photo session to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog({ open: true })}
          >
            Create Session
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {sessions.map((session) => (
            <Grid item xs={12} sm={6} md={4} key={session.uniqueId}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s'
                  }
                }}
                onClick={() => handleSelectSession(session)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h2" noWrap sx={{ flexGrow: 1 }}>
                      {session.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, session)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {session.description && (
                    <Typography variant="body2" color="text.secondary" paragraph sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {session.description}
                    </Typography>
                  )}

                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      icon={<PhotoLibraryIcon />}
                      label={`${session.formattedStats?.totalImages || 0} images`}
                      size="small"
                      variant="outlined"
                    />
                    {session.settings?.eventType && (
                      <Chip
                        label={session.settings.eventType}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      label={session.status || 'active'}
                      size="small"
                      color={session.status === 'active' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      <CalendarIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Created: {formatDate(session.createdAt)}
                    </Typography>
                    {session.formattedStats?.totalSizeFormatted && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        <StorageIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                        Size: {session.formattedStats.totalSizeFormatted}
                      </Typography>
                    )}
                  </Box>
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<PhotoLibraryIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectSession(session);
                    }}
                  >
                    Open Session
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PhotoAlbumIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartAlbumWizard(session);
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    Create Album
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleStartAlbumWizard()}>
          <PhotoAlbumIcon sx={{ mr: 1 }} color="primary" />
          Album Wizard
        </MenuItem>
        <MenuItem onClick={handleOpenFaceRecognition}>
          <FolderIcon sx={{ mr: 1 }} />
          Face Recognition
        </MenuItem>
        <MenuItem onClick={handleCreateAlbum}>
          <PhotoAlbumIcon sx={{ mr: 1 }} />
          Create Album (Advanced)
        </MenuItem>
        <MenuItem onClick={() => {
          setDeleteDialog({ open: true, session: selectedSession });
          handleMenuClose();
        }}>
          <DeleteIcon sx={{ mr: 1 }} color="error" />
          Delete Session
        </MenuItem>
      </Menu>

      {/* Create Session Dialog */}
      <Dialog
        open={createDialog.open}
        onClose={() => setCreateDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name *"
            fullWidth
            value={newSession.name}
            onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
            placeholder="e.g., Summer Vacation 2026"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newSession.description}
            onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
            placeholder="Optional description of this photo session"
          />
          <TextField
            margin="dense"
            label="Event Type"
            fullWidth
            value={newSession.eventType}
            onChange={(e) => setNewSession({ ...newSession, eventType: e.target.value })}
            placeholder="e.g., Wedding, Birthday, Travel"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog({ open: false })}>
            Cancel
          </Button>
          <Button onClick={handleCreateSession} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleteProgress.isDeleting && setDeleteDialog({ open: false, session: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {deleteProgress.isDeleting || deleteProgress.steps.length > 0 ? 'Deleting Session...' : 'Delete Session?'}
        </DialogTitle>
        <DialogContent>
          {!deleteProgress.isDeleting && deleteProgress.steps.length === 0 ? (
            <>
              <Typography>
                Are you sure you want to delete "{deleteDialog.session?.name}"?
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  This will permanently delete:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• All images from Azure Blob Storage" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• All image records from database" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• All embeddings from database" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• All reference persons from database" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• AWS Rekognition collection and faces" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• The session record" />
                  </ListItem>
                </List>
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  This action cannot be undone.
                </Typography>
              </Alert>
            </>
          ) : (
            <Box>
              {deleteProgress.isDeleting && deleteProgress.steps.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Initializing deletion...</Typography>
                </Box>
              )}

              {deleteProgress.steps.length > 0 && (
                <List>
                  {deleteProgress.steps.map((step, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {getStatusIcon(step.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={step.step}
                        secondary={
                          step.details && Object.keys(step.details).length > 0 ? (
                            <Box component="span" sx={{ fontSize: '0.85rem' }}>
                              {step.details.imageCount !== undefined && `Images: ${step.details.imageCount}`}
                              {step.details.embeddingCount !== undefined && `Embeddings: ${step.details.embeddingCount}`}
                              {step.details.referencePersonCount !== undefined && `Reference Persons: ${step.details.referencePersonCount}`}
                              {step.details.successCount !== undefined && ` (${step.details.successCount} succeeded`}
                              {step.details.failureCount !== undefined && `, ${step.details.failureCount} failed)`}
                              {step.details.deletedCount !== undefined && `Deleted: ${step.details.deletedCount}`}
                              {step.details.collectionId && ` (${step.details.collectionId})`}
                              {step.details.sessionName && `Session: ${step.details.sessionName}`}
                              {step.details.error && (
                                <Typography color="error" variant="caption" display="block">
                                  Error: {step.details.error}
                                </Typography>
                              )}
                              {step.details.warning && (
                                <Typography color="warning.main" variant="caption" display="block">
                                  Warning: {step.details.warning}
                                </Typography>
                              )}
                              {step.details.reason && ` - ${step.details.reason}`}
                            </Box>
                          ) : null
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {deleteProgress.isDeleting && (
                <LinearProgress sx={{ mt: 2 }} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!deleteProgress.isDeleting && deleteProgress.steps.length === 0 && (
            <>
              <Button onClick={() => setDeleteDialog({ open: false, session: null })}>
                Cancel
              </Button>
              <Button onClick={handleDeleteSession} color="error" variant="contained">
                Delete Permanently
              </Button>
            </>
          )}
          {!deleteProgress.isDeleting && deleteProgress.steps.length > 0 && (
            <Button 
              onClick={() => {
                setDeleteDialog({ open: false, session: null });
                setDeleteProgress({ steps: [], isDeleting: false });
              }}
              variant="contained"
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SessionManagementPage;
